import { useNavigate, Outlet } from 'react-router';
import { AppShell, DropdownMenu, type DropdownItem } from '../../components';
import { useCurrentProject } from '../project-context';
import { ProjectProvider } from '../ProjectProvider';
import { ProjectSelect } from '../ProjectSelect';
import { ErrorBoundary } from '../ErrorBoundary';
import { RequireAccess } from '../guards';
import { navRoutes, sidebarItems } from '../nav';
import { breadcrumbsFor, useCurrentRoute, usePageAnnouncements } from '../route-meta';
import { useSession } from '../session-context';
import { AppFooter } from './AppFooter';

/**
 * Authenticated layout: navbar (project selector + user menu), sidebar, breadcrumbs, page title,
 * content, footer. Legacy layout_page_begin()/layout_page_end().
 */
export function AppLayout() {
  return (
    <RequireAccess access="user">
      <ProjectProvider>
        <AppLayoutInner />
      </ProjectProvider>
    </RequireAccess>
  );
}

function AppLayoutInner() {
  const { state, logout } = useSession();
  const navigate = useNavigate();
  const current = useCurrentRoute();
  const { projectId, setProjectId } = useCurrentProject();
  const user = state.status === 'authenticated' ? state.user : null;
  usePageAnnouncements(current?.title);

  const accountItems: DropdownItem[] = [
    ...navRoutes('account', user).map((r) => ({
      label: r.title,
      onSelect: () => void navigate(r.path),
    })),
    { type: 'separator' as const },
    {
      label: 'Logout',
      onSelect: () => {
        void logout().then(() => navigate('/login'));
      },
    },
  ];

  return (
    <AppShell
      nav={sidebarItems(user)}
      title={current?.title}
      breadcrumbs={current ? breadcrumbsFor(current) : undefined}
      headerCenter={
        <div className="hidden w-56 md:block">
          {/* legacy set_project.php: the current project scopes lists, reports and new issues */}
          <ProjectSelect value={projectId} onChange={setProjectId} />
        </div>
      }
      headerEnd={
        <DropdownMenu
          inverse
          align="end"
          label={user?.username ?? 'Account'}
          items={accountItems}
        />
      }
      footer={<AppFooter />}
    >
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
    </AppShell>
  );
}
